import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CommentBody, ThreadData } from "@liveblocks/core";
import { useSelf, useThreadSubscription, useThreads } from "@liveblocks/react";
import { useCommentsStore } from "../../../../../../store/comments";
import { CommentBodyRenderer } from "./CommentBodyRenderer";
import { Avatar } from "./CommentItem";
import { useLiveblocksUser } from "./useLiveblocksUser";
import { computeIsUnread } from "./unreadStatus";
import CheckedBoxIcon from "components/icons/components/CheckedBoxIcon";
import UncheckedBoxIcon from "components/icons/components/UncheckedBoxIcon";
import { formatRelative } from "./utils";

/** Flatten a Liveblocks `CommentBody` into plain text for search matching. */
const extractBodyText = (body: CommentBody | undefined | null): string => {
  if (!body?.content?.length) return "";
  const parts: string[] = [];
  body.content.forEach((block: any) => {
    if (block?.type !== "paragraph") return;
    (block.children || []).forEach((inline: any) => {
      if (!inline) return;
      if ("text" in inline) parts.push(String(inline.text || ""));
      else if (inline.type === "mention" && inline.id) parts.push(`@${inline.id}`);
      else if (inline.type === "link") parts.push(String(inline.text || inline.url || ""));
    });
    parts.push(" "); // paragraph separator
  });
  return parts.join("").toLowerCase();
};

const ThreadRow: React.FC<{
  thread: ThreadData;
  isActive: boolean;
  threadNumber: number;
  onClick: () => void;
}> = ({ thread, isActive, threadNumber, onClick }) => {
  const first = thread.comments.find((c) => c.body) || thread.comments[0];
  const user = useLiveblocksUser(first?.userId);
  const self = useSelf();
  const authorDisplayName = user.name;
  const replyCount = Math.max(0, thread.comments.filter((c) => c.body).length - 1);
  const isResolved = !!thread.resolved;
  const subscription = useThreadSubscription(thread.id);
  const localReadAt = useCommentsStore((s) => s.threadReadAt[thread.id]);

  const isUnread = useMemo(
    () => computeIsUnread(thread, self?.id, subscription, localReadAt),
    [thread, self?.id, subscription, localReadAt]
  );

  // NOTE: previously we auto-cleared `unreadSince` when the only activity in
  // a thread was the current user's own comment, but that effect kept entering
  // a re-render loop (the optimistic update from `markThreadAsRead` re-notifies
  // every subscriber, which produces a fresh `unreadSince` Date instance and
  // re-fires the effect → "Maximum update depth exceeded"). The cosmetic gain
  // wasn't worth the catastrophic crash, so we no longer touch the Liveblocks
  // unread cursor here. Local `threadReadAt` already covers the "self-only"
  // case via `computeIsUnread`.

  const showUnread = isUnread && !isResolved;

  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer border-b border-[#E2E2FC] px-3 py-3 transition-colors ${
        isActive ? "bg-[#F4F4FB]" : showUnread ? "bg-[#FEF4F6] hover:bg-[#FCEBEF]" : "hover:bg-[#FAFAFD]"
      } ${isResolved ? "opacity-60" : ""}`}
    >
      {/* Left accent bar for unread threads */}
      {showUnread && <span aria-hidden className="absolute left-0 top-0 h-full w-[3px] bg-[#EE4360]" />}
      <div className="flex gap-2">
        <div className="shrink-0 pt-0.5">{first?.userId ? <Avatar userId={first.userId} size={24} /> : null}</div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[#130f26]/40">#{threadNumber}</div>
          <div className="flex items-baseline gap-2">
            <span className={`truncate text-[12px] text-[#130f26] ${showUnread ? "font-bold" : "font-semibold"}`}>
              {authorDisplayName}
            </span>
            <span className="text-[10px] text-[#130f26]/50">{first ? formatRelative(first.createdAt) : ""}</span>
            {showUnread && (
              <span className="ml-auto inline-block h-2 w-2 shrink-0 rounded-full bg-[#EE4360]" aria-label="Unread" />
            )}
            {isResolved && (
              <span className="ml-auto rounded-full bg-[#d1fae5] px-1.5 py-[1px] text-[9px] text-[#047857]">
                Resolved
              </span>
            )}
          </div>
          <div className={`mt-0.5 line-clamp-2 text-[12px] ${showUnread ? "text-[#130f26]" : "text-[#130f26]/80"}`}>
            {first?.body ? <CommentBodyRenderer body={first.body} /> : <em className="opacity-60">(empty)</em>}
          </div>
          {replyCount > 0 && (
            <div className={`mt-1 text-[11px] ${showUnread ? "font-semibold text-[#EE4360]" : "text-[#4f5b94]"}`}>
              {replyCount} {replyCount === 1 ? "reply" : "replies"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type FilterMenuProps = {
  anchorRect: DOMRect;
  sortBy: "date" | "unread";
  onlyMine: boolean;
  showResolved: boolean;
  onSortChange: (v: "date" | "unread") => void;
  onToggleOnlyMine: () => void;
  onToggleShowResolved: () => void;
  onClose: () => void;
};

const FilterMenu: React.FC<FilterMenuProps> = ({
  anchorRect,
  sortBy,
  onlyMine,
  showResolved,
  onSortChange,
  onToggleOnlyMine,
  onToggleShowResolved,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: anchorRect.bottom + 6,
    left: anchorRect.left,
  });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    let left = anchorRect.left;
    // Keep inside viewport on the right edge
    if (left + rect.width > vw - 8) left = Math.max(8, vw - rect.width - 8);
    setPos({ top: anchorRect.bottom + 6, left });
  }, [anchorRect.left, anchorRect.bottom]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current && !ref.current.contains(t) && !t.closest("[data-comments-filter-button]")) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      data-comment-portal
      role="menu"
      className="fixed z-[2147483647] w-[200px] overflow-hidden rounded-lg border border-solid border-[#E2E2FC] bg-white shadow-[0_12px_32px_rgba(16,24,40,0.12)]"
      style={{ top: pos.top, left: pos.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#130f26]/40">Sort</div>
      <button
        type="button"
        onClick={() => {
          onSortChange("date");
          onClose();
        }}
        className={`flex w-full items-center justify-between px-3 py-2 text-left text-[12px] transition-colors hover:bg-[#F4F4FB] ${
          sortBy === "date" ? "text-[#130f26]" : "text-[#130f26]/80"
        }`}
      >
        <span>Sort by date</span>
        {sortBy === "date" && (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#4f5b94" strokeWidth="2">
            <path d="M3.5 8.5l3 3 6-6.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={() => {
          onSortChange("unread");
          onClose();
        }}
        className={`flex w-full items-center justify-between px-3 py-2 text-left text-[12px] transition-colors hover:bg-[#F4F4FB] ${
          sortBy === "unread" ? "text-[#130f26]" : "text-[#130f26]/80"
        }`}
      >
        <span>Sort by unread</span>
        {sortBy === "unread" && (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#4f5b94" strokeWidth="2">
            <path d="M3.5 8.5l3 3 6-6.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <div className="my-1 border-t border-[#F4F4F7]" />

      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#130f26]/40">Filter</div>
      <div
        role="menuitemcheckbox"
        aria-checked={onlyMine}
        onClick={onToggleOnlyMine}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-[12px] text-[#130f26]/80 transition-colors hover:bg-[#F4F4FB]"
      >
        <span className="flex items-center">{onlyMine ? <CheckedBoxIcon /> : <UncheckedBoxIcon />}</span>
        <span>Only your threads</span>
      </div>
      <div
        role="menuitemcheckbox"
        aria-checked={showResolved}
        onClick={onToggleShowResolved}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-[12px] text-[#130f26]/80 transition-colors hover:bg-[#F4F4FB]"
      >
        <span className="flex items-center">{showResolved ? <CheckedBoxIcon /> : <UncheckedBoxIcon />}</span>
        <span>Show resolved</span>
      </div>
    </div>,
    document.body
  );
};

// A wrapper row that also reports its unread status upward so the parent can
// use it for sorting. Mirrors the logic in ThreadRow (see computeIsUnread).
const SubscriptionReporter: React.FC<{
  thread: ThreadData;
  onReport: (threadId: string, unread: boolean) => void;
}> = ({ thread, onReport }) => {
  const subscription = useThreadSubscription(thread.id);
  const self = useSelf();
  const localReadAt = useCommentsStore((s) => s.threadReadAt[thread.id]);
  const unread = computeIsUnread(thread, self?.id, subscription, localReadAt);
  useEffect(() => {
    onReport(thread.id, unread);
  }, [thread.id, unread, onReport]);
  return null;
};

export const ThreadsListPanel: React.FC = () => {
  const threadsResult = useThreads();
  // Normalize to a concrete array — `threadsResult.threads` is typed as
  // possibly undefined, which propagates into every downstream consumer.
  const threads: ThreadData[] = threadsResult.isLoading ? [] : threadsResult.threads ?? [];
  const isLoading = threadsResult.isLoading;
  const showResolved = useCommentsStore((s) => s.showResolved);
  const toggleShowResolved = useCommentsStore((s) => s.toggleShowResolved);
  const sortBy = useCommentsStore((s) => s.sortBy);
  const setSortBy = useCommentsStore((s) => s.setSortBy);
  const onlyMine = useCommentsStore((s) => s.onlyMine);
  const toggleOnlyMine = useCommentsStore((s) => s.toggleOnlyMine);
  const openThreadId = useCommentsStore((s) => s.openThreadId);
  const focusThread = useCommentsStore((s) => s.focusThread);
  const isPlacing = useCommentsStore((s) => s.isPlacing);
  const self = useSelf();

  const [filterAnchor, setFilterAnchor] = useState<DOMRect | null>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  // Search query for filtering threads by comment text. Case-insensitive,
  // matches on all comments within a thread.
  const [searchQuery, setSearchQuery] = useState("");
  const searchTerm = searchQuery.trim().toLowerCase();

  // Track unread status per thread to enable "Sort by unread"
  const [unreadMap, setUnreadMap] = useState<Record<string, boolean>>({});
  const reportUnread = React.useCallback((threadId: string, unread: boolean) => {
    setUnreadMap((prev) => (prev[threadId] === unread ? prev : { ...prev, [threadId]: unread }));
  }, []);

  const filteredSorted = useMemo(() => {
    let list = (threads || []).filter((t) => (showResolved ? true : !t.resolved));
    if (onlyMine && self?.id) {
      list = list.filter((t) => t.comments.some((c) => c.userId === self.id));
    }
    if (searchTerm) {
      list = list.filter((t) => t.comments.some((c) => extractBodyText(c.body).includes(searchTerm)));
    }
    const sorted = [...list].sort((a, b) => {
      if (sortBy === "unread") {
        const aUnread = unreadMap[a.id] ? 1 : 0;
        const bUnread = unreadMap[b.id] ? 1 : 0;
        if (aUnread !== bUnread) return bUnread - aUnread;
        // tie-breaker: most recent activity first
        const aTs = new Date(a.updatedAt || a.createdAt).getTime();
        const bTs = new Date(b.updatedAt || b.createdAt).getTime();
        return bTs - aTs;
      }
      const aTs = new Date(a.createdAt).getTime();
      const bTs = new Date(b.createdAt).getTime();
      return bTs - aTs;
    });
    return sorted;
  }, [threads, showResolved, onlyMine, self?.id, sortBy, unreadMap, searchTerm]);

  // Stable thread numbers: oldest thread is #1, next #2, etc. Computed
  // against the full thread list (not the filtered one) so numbers don't
  // shift when the user toggles filters or searches.
  const threadNumberById = useMemo(() => {
    const ordered = [...(threads || [])].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const map: Record<string, number> = {};
    ordered.forEach((t, i) => {
      map[t.id] = i + 1;
    });
    return map;
  }, [threads]);

  const openFilter = () => {
    if (!filterBtnRef.current) return;
    setFilterAnchor(filterBtnRef.current.getBoundingClientRect());
  };

  const filterActiveCount = (onlyMine ? 1 : 0) + (sortBy !== "date" ? 1 : 0) + (showResolved ? 1 : 0);

  // Global unread count — same value used by the tool island badge, computed
  // by the always-mounted aggregator in CommentsOverlay.
  const unreadCount = useCommentsStore((s) => s.totalUnreadCount);

  return (
    <div data-comment-selectable className="flex h-full flex-col" onContextMenu={(e) => e.stopPropagation()}>
      {/* Hidden reporters so the parent can know which threads are unread */}
      {threads.map((t) => (
        <SubscriptionReporter key={`sub-${t.id}`} thread={t} onReport={reportUnread} />
      ))}

      <div className="flex items-center gap-2 border-b border-[#E2E2FC] px-3 py-2">
        {/* Search bar — matches against comment text within each thread. */}
        <div className="relative flex min-w-0 flex-1 items-center">
          <svg
            aria-hidden
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            className="pointer-events-none absolute left-2 text-[#130f26]/40"
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5L14 14" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search comments…"
            className="h-7 w-full rounded border border-solid border-[#E2E2FC] bg-white pl-7 pr-6 text-[11px] text-[#130f26] outline-none transition-colors placeholder:text-[#130f26]/40 focus:border-[#4f5b94]"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-1.5 flex h-4 w-4 items-center justify-center rounded text-[#130f26]/40 hover:bg-[#F4F4FB] hover:text-[#130f26]"
              aria-label="Clear search"
              title="Clear search"
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        <button
          ref={filterBtnRef}
          type="button"
          data-comments-filter-button
          onClick={() => (filterAnchor ? setFilterAnchor(null) : openFilter())}
          className={`relative flex h-7 shrink-0 items-center justify-center rounded border border-solid px-1.5 transition-colors ${
            filterAnchor
              ? "border-[#dadaf2] bg-[#F4F4FB] text-[#130f26]"
              : "border-[#E2E2FC] bg-white text-[#130f26]/70 hover:bg-[#FAFAFD]"
          }`}
          aria-haspopup="menu"
          aria-expanded={!!filterAnchor}
          aria-label="Filter"
          title="Filter"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M2.5 4h11M4.5 8h7M6.5 12h3" strokeLinecap="round" />
          </svg>
          {filterActiveCount > 0 && (
            <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#4f5b94] px-1 text-[9px] font-semibold text-white">
              {filterActiveCount}
            </span>
          )}
        </button>
      </div>

      {/* Unread badge on its own row so the header stays compact. */}
      {unreadCount > 0 && (
        <div className="flex items-center border-b border-[#E2E2FC] bg-[#FEF4F6]/40 px-3 py-1.5">
          <span
            className="inline-flex h-5 items-center gap-1 rounded-full bg-[#FEF4F6] px-2 text-[10px] font-semibold text-[#EE4360]"
            aria-label={`${unreadCount} unread`}
            title={`${unreadCount} unread thread${unreadCount === 1 ? "" : "s"}`}
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#EE4360]" />
            {unreadCount} new
          </span>
        </div>
      )}

      {filterAnchor && (
        <FilterMenu
          anchorRect={filterAnchor}
          sortBy={sortBy}
          onlyMine={onlyMine}
          showResolved={showResolved}
          onSortChange={setSortBy}
          onToggleOnlyMine={toggleOnlyMine}
          onToggleShowResolved={toggleShowResolved}
          onClose={() => setFilterAnchor(null)}
        />
      )}

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-[12px] text-[#130f26]/50">Loading comments…</div>
        ) : filteredSorted.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-[#130f26]/50">
            {isPlacing
              ? "Click anywhere on the canvas to drop a comment."
              : onlyMine
              ? "You haven't participated in any threads yet."
              : "No comments yet."}
          </div>
        ) : (
          filteredSorted.map((thread) => (
            <ThreadRow
              key={thread.id}
              thread={thread}
              threadNumber={threadNumberById[thread.id] ?? 0}
              isActive={openThreadId === thread.id}
              onClick={() => focusThread(thread.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ThreadsListPanel;
